/**
 * Skeleton Locations Drawer Component
 *
 * Lightweight skeleton that matches the real local-pickup-view layout.
 * Shows instantly while the full map bundle loads.
 *
 * @element skeleton-locations-drawer
 *
 * @remarks
 * Mobile: Full-screen layout with header, location cards, and tab bar.
 * Desktop: Fixed sidebar with header and location cards.
 *
 * @example
 * ```html
 * <skeleton-locations-drawer></skeleton-locations-drawer>
 * ```
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import peachLogoUrl from "/logo-footer-red.svg?url";

@customElement("skeleton-locations-drawer")
export class SkeletonLocationsDrawer extends LitElement {
  /**
   * Logo URL - imported via Vite for proper bundling
   */
  private readonly logoUrl = peachLogoUrl;

  // No custom styles needed - using Tailwind's animate-pulse
  static styles = css``;

  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  render() {
    return html`
      <!-- Desktop Layout: Fixed sidebar -->
      <aside
        class="hidden md:flex bg-cream shadow-2xl z-40 fixed left-0 top-0 bottom-0 w-96 flex-col"
        role="complementary"
        aria-label="Loading locations"
      >
        ${this.renderDesktopHeader()}
        ${this.renderLocationsList()}
      </aside>

      <!-- Mobile Layout: Full-screen with header, list, and tab bar -->
      <div
        class="md:hidden fixed inset-0 flex flex-col bg-cream z-40"
        role="complementary"
        aria-label="Loading locations"
      >
        ${this.renderMobileHeader()}
        ${this.renderMobileLocationsList()}
        ${this.renderMobileTabBar()}
      </div>
    `;
  }

  /**
   * Render mobile header (matches local-pickup-view renderMobileHeader)
   */
  private renderMobileHeader() {
    return html`
      <header class="flex flex-col items-center px-4 pt-3 pb-4 bg-cream flex-shrink-0 border-b border-default">
        <div class="flex items-center justify-between w-full mb-4">
          <a href="/" class="focus:outline-none focus:ring-2 focus:ring-primary rounded">
            <img
              src="${this.logoUrl}"
              alt="The Peach Truck"
              class="h-[40px] w-auto"
            />
          </a>
        </div>
        <!-- Search bar skeleton -->
        <div class="w-full">
          <div class="h-12 w-full rounded-lg bg-skeleton animate-pulse"></div>
        </div>
      </header>
    `;
  }

  /**
   * Render desktop header (matches locations-drawer renderHeader)
   */
  private renderDesktopHeader() {
    return html`
      <header class="flex flex-col items-center px-4 pt-3 pb-4 bg-cream border-b border-default">
        <div class="flex items-center justify-between w-full mb-4">
          <a href="/" class="focus:outline-none focus:ring-2 focus:ring-primary rounded">
            <img
              src="${this.logoUrl}"
              alt="The Peach Truck"
              class="h-[40px] w-auto"
            />
          </a>
          <!-- View toggle skeleton -->
          <div class="h-9 w-36 rounded-lg bg-skeleton animate-pulse"></div>
        </div>
        <!-- Search bar skeleton -->
        <div class="w-full">
          <div class="h-12 w-full rounded-lg bg-skeleton animate-pulse"></div>
        </div>
      </header>
    `;
  }

  /**
   * Render mobile locations list (with bottom padding for tab bar)
   */
  private renderMobileLocationsList() {
    return html`
      <div class="flex-1 overflow-y-auto px-4 py-4 pb-20">
        <!-- Location count skeleton -->
        <div class="mb-4 animate-pulse">
          <div class="h-7 w-36 rounded-full bg-skeleton"></div>
        </div>

        <div class="flex flex-col gap-3">
          ${this.renderLocationCard(0)}
          ${this.renderLocationCard(1)}
          ${this.renderLocationCard(2)}
          ${this.renderLocationCard(3)}
          ${this.renderLocationCard(4)}
          ${this.renderLocationCard(5)}
        </div>
      </div>
    `;
  }

  /**
   * Render desktop locations list
   */
  private renderLocationsList() {
    return html`
      <div class="flex-1 overflow-hidden mt-1">
        <div class="overflow-y-auto h-full px-4 py-1">
          <div class="mb-4 animate-pulse">
            <div class="h-7 w-36 rounded-full bg-skeleton"></div>
          </div>

          <div class="flex flex-col gap-3">
            ${this.renderLocationCard(0)}
            ${this.renderLocationCard(1)}
            ${this.renderLocationCard(2)}
            ${this.renderLocationCard(3)}
            ${this.renderLocationCard(4)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render mobile tab bar skeleton (matches local-pickup-view renderTabBar)
   */
  private renderMobileTabBar() {
    return html`
      <nav
        class="fixed bottom-0 left-0 right-0 z-50 bg-cream border-t border-default"
        aria-label="Main navigation"
      >
        <div class="flex items-stretch">
          <!-- Browse tab (active) -->
          <div class="flex-1 flex flex-col items-center gap-0.5 py-3">
            <div class="h-6 w-6 rounded bg-skeleton animate-pulse"></div>
            <div class="h-3 w-10 rounded bg-skeleton animate-pulse"></div>
          </div>
          <!-- Calendar tab -->
          <div class="flex-1 flex flex-col items-center gap-0.5 py-3">
            <div class="h-6 w-6 rounded bg-skeleton animate-pulse"></div>
            <div class="h-3 w-14 rounded bg-skeleton animate-pulse"></div>
          </div>
          <!-- Cart tab -->
          <div class="flex-1 flex flex-col items-center gap-0.5 py-3">
            <div class="h-6 w-6 rounded bg-skeleton animate-pulse"></div>
            <div class="h-3 w-8 rounded bg-skeleton animate-pulse"></div>
          </div>
        </div>
      </nav>
    `;
  }

  /**
   * Render individual location card skeleton
   */
  private renderLocationCard(index = 0) {
    const dateBadges = index % 2 === 0 ? 3 : 2;
    const nameWidths = ['w-3/4', 'w-2/3', 'w-4/5', 'w-5/6'];
    const nameWidth = nameWidths[index % nameWidths.length];
    const addressWidths = ['w-3/4', 'w-2/3', 'w-4/5'];
    const addressWidth = addressWidths[index % addressWidths.length];

    return html`
      <div class="border-2 border-muted rounded-lg p-4 bg-surface">
        <div class="flex items-start justify-between mb-2">
          <div class="h-5 ${nameWidth} rounded-full bg-skeleton animate-pulse"></div>
          <div class="h-5 w-16 rounded-full bg-skeleton animate-pulse"></div>
        </div>
        <div class="h-4 ${addressWidth} rounded-full bg-skeleton animate-pulse mb-1"></div>
        <div class="flex flex-wrap items-center gap-1">
          <div class="h-4 w-4 rounded-full bg-skeleton animate-pulse"></div>
          ${Array.from({ length: dateBadges }).map(() => html`
            <div class="h-6 w-20 rounded-full bg-skeleton animate-pulse"></div>
          `)}
        </div>
      </div>
    `;
  }
}
